<template>
  <div class="case-copy-tool new-globals">
    <div class="selector-container">
      <user-case-selector
        title="From"
        @envSelected="updateFromEnv"
        @orgSelected="updateSelectedFromOrg"
        @userSelected="updateSelectedFromUser"
        @casesSelected="updateSelectedCases"
      />
    </div>
    <div class="selector-container">
      <user-case-selector
        title="To"
        :disable-case-selection="true"
        :loading="copying"
        @envSelected="updateToEnv"
        @orgSelected="updateSelectedToOrg"
        @userSelected="updateSelectedToUser"
      />
      <md-button
        class="md-primary md-raised copy-button"
        :disabled="!state.selectedFromOrg || !state.selectedToOrg || !state.selectedFromUser || !state.selectedToUser || !state.selectedCaseIds || !state.selectedCaseIds.length"
        @click="copy"
      >
        Copy
      </md-button>
      <md-checkbox v-if="hasDiffError" v-model="state.ignoreDiffs">
        Ignore differences
      </md-checkbox>
      <span v-if="hasDiffError" class="diff-warning">
        The case you attempted to transfer had a difference in the target environment.
        This can be due to a difference in the data format supported in each environment.
        By checking the box above and clicking copy again, you will force the transfer to happen.
        However, the target case may not function correctly.  Proceed with caution.
      </span>
    </div>

    <span v-if="error && error.code !== 'CASE_JSON_NOT_EQUAL'" class="error">{{
      error.message || error.error || error
    }}</span>

    <md-card v-if="error && error.code === 'CASE_JSON_NOT_EQUAL'" class="case-json-diff-container">
      <md-card-header>
        <div class="md-title">Case Copy Diff - {{ JSON.parse(error.dumpedCaseJson).name }}</div>
      </md-card-header>
      <md-card-content>
        <code-diff class="code-diff" :old-string="error.dumpedCaseJson" :new-string="error.restoredCaseJson" />
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'
import CodeDiff from 'vue-code-diff'
import UserCaseSelector from '../UserCaseSelector';

export default {
  name: 'case-copy-tool',
  components: {
    CodeDiff,
    UserCaseSelector,
  },
  props: {
    fromEnv: {
      type: String,
      default: 'dev'
    },
    toEnv: {
      type: String,
      default: 'dev'
    },
    fromOrgs: {
      type: Array,
      default: () => []
    },
    fromCases: {
      type: Array,
      default: () => []
    },
    fromUsers: {
      type: Array,
      default: () => []
    },
    toOrgs: {
      type: Array,
      default: () => []
    },
    toUsers: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    copying: {
      type: Boolean,
      default: false
    },
    error: {
      type: [Error, Object],
      default: null
    }
  },
  data() {
    return {
      state: {
        selectedFromOrg: null,
        selectedFromUser: null,
        selectedToOrg: null,
        selectedToUser: null,
        selectedCaseIds: null,
        searchTerm: '',
        ignoreDiffs: false
      }
    };
  },
  computed: {
    fromUsersWithLabel() {
      return this.fromUsers.map(user => ({
        ...user,
        label: this.userLabel(user)
      }))
    },
    toUsersWithLabel() {
      return this.toUsers.map(user => ({
        ...user,
        label: this.userLabel(user)
      }))
    },
    filteredCases() {
      if (this.state.searchTerm) {
        return this.fromCases.filter(caseConf =>
          caseConf.name
            .toLowerCase()
            .includes(this.state.searchTerm.trim().toLowerCase())
        );
      }
      return this.fromCases;
    },
    isAllCases: {
      get() {
        const selectedIds = sortBy(this.state.selectedCaseIds);
        const filteredIds = sortBy(this.filteredCases.map(caseConf => caseConf.id));
        return isEqual(selectedIds, filteredIds);
      },
      set() {
        if (this.isAllCases) {
          this.unselectAllCases();
        } else {
          this.selectAllCases();
        }
      },
    },
    hasDiffError() {
      return this.error && this.error.code === 'CASE_JSON_NOT_EQUAL';
    }
  },
  watch: {
    copying(newVal, oldVal) {
      // Reset the selected case when copying is successful
      if (!newVal && oldVal && !this.error) {
        alert('Cases successfully copied');
        this.state.selectedCaseIds = null;
      }
    }
  },
  methods: {
    userLabel(user) {
      if (user.given_name || user.family_name) {
        return `${user.given_name || ''} ${user.family_name || ''}`;
      }
      return user.email;
    },
    updateFromEnv(env) {
      this.$emit('updateFromEnv', env);
    },
    updateToEnv(env) {
      this.$emit('updateToEnv', env);
    },
    updateSelectedFromOrg(org) {
      this.state.selectedFromOrg = org;
    },
    updateSelectedFromUser(user) {
      this.state.selectedFromUser = user;
    },
    updateSelectedToUser(user) {
      this.state.selectedToUser = user;
    },
    updateSelectedToOrg(org) {
      this.state.selectedToOrg = org;
    },
    updateSelectedCases(caseIds) {
      this.state.selectedCaseIds = caseIds;
    },
    selectAllCases() {
      this.updateSelectedCases(
        this.filteredCases.map(caseConf => caseConf.id)
      );
    },
    unselectAllCases() {
      this.updateSelectedCases([]);
    },
    copy() {
      this.$emit(
        'copy',
        this.state.selectedCaseIds,
        this.state.selectedFromOrg,
        this.state.selectedToOrg,
        this.state.selectedFromUser,
        this.state.selectedToUser,
        this.state.ignoreDiffs
      );
      this.state.ignoreDiffs = false; // Force user to recheck the ignore diffs box
    }
  }
};
</script>

<style scoped>
.case-copy-tool {
  display: flex;
  flex-wrap: wrap;
}

.selector-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.selection-card {
  max-width: 400px;
}

.error {
  color: red;
}

.case-json-diff-container {
  max-width: 600px;
}

.code-diff {
  max-width: 100%;
  height: 500px !important;
  overflow: auto;
}

.diff-warning {
  font-style: italic;
  width: 270px;
  margin-bottom: 10px;
}
</style>